import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  disableMfa,
  fetchMfaStatus,
  MfaStatus,
  regenerateBackupCodes,
  storeBackupCodesOnce,
} from '../../services/mfa.ts';

const formatDate = (iso: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return null;
  }
};

const SecuritySettings: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchMfaStatus();
      setStatus(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Erro ao carregar status MFA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForms = () => {
    setPassword('');
    setCode('');
    setFormError('');
    setShowDisable(false);
    setShowRegen(false);
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !code) {
      setFormError('Preencha senha e código.');
      return;
    }
    try {
      setBusy(true);
      setFormError('');
      await disableMfa(password, code);
      resetForms();
      await load();
    } catch (err: any) {
      setFormError(
        err?.response?.data?.error || err?.message || 'Não foi possível desativar o MFA.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRegen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !code) {
      setFormError('Preencha senha e código.');
      return;
    }
    try {
      setBusy(true);
      setFormError('');
      const result = await regenerateBackupCodes(password, code);
      storeBackupCodesOnce(result.backupCodes);
      resetForms();
      navigate('/settings/security/mfa/backup', { state: { from: 'regenerate' } });
    } catch (err: any) {
      setFormError(
        err?.response?.data?.error || err?.message || 'Não foi possível regenerar os códigos.'
      );
    } finally {
      setBusy(false);
    }
  };

  const enabledAt = formatDate(status?.enabledAt || null);
  const regenAt = formatDate(status?.backupCodesGeneratedAt || null);

  return (
    <div className="form-page mfa-layout">
      <div className="breadcrumb" style={{ marginBottom: 4 }}>
        Config / Preferências / Segurança
      </div>
      <h1 className="page-title" style={{ marginBottom: 4 }}>
        Segurança da conta
      </h1>
      <div className="page-subtitle" style={{ marginBottom: 16 }}>
        Autenticação em duas etapas e códigos de recuperação
      </div>

      {loading && <div className="hint-banner">Carregando…</div>}
      {error && (
        <div className="auth-error" style={{ marginBottom: 16 }}>
          <strong>Erro</strong>
          {error}
        </div>
      )}

      {!loading && status && (
        <>
          <div className="form-card">
            <div className="form-card-body">
              <div className="form-section-title">Autenticação em duas etapas</div>

              <div className="status-row">
                <div className="status-meta">
                  <strong>{status.mfaEnabled ? 'MFA ativo' : 'MFA desativado'}</strong>
                  <span>
                    {status.mfaEnabled
                      ? `Aplicativo autenticador${enabledAt ? ` · ativado em ${enabledAt}` : ''}`
                      : 'Proteja sua conta com um aplicativo autenticador (TOTP)'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {status.mfaEnabled ? (
                    <span className="chip chip-green">
                      <span className="chip-dot" />
                      Ativo
                    </span>
                  ) : (
                    <span className="chip chip-slate">Inativo</span>
                  )}
                  <button
                    type="button"
                    className={`toggle${status.mfaEnabled ? '' : ' off'}`}
                    aria-label={status.mfaEnabled ? 'MFA ativo' : 'MFA inativo'}
                    title={status.mfaEnabled ? 'Ligado' : 'Desligado'}
                    onClick={() => {
                      if (status.mfaEnabled) {
                        setShowDisable(true);
                        setShowRegen(false);
                        setFormError('');
                      } else {
                        navigate('/settings/security/mfa/setup');
                      }
                    }}
                  />
                </div>
              </div>

              {status.mfaEnabled ? (
                <>
                  <div className="status-row">
                    <div className="status-meta">
                      <strong>Códigos de recuperação</strong>
                      <span>
                        {status.backupCodesRemaining} de 8 códigos restantes
                        {regenAt ? ` · última regeneração em ${regenAt}` : ''}
                      </span>
                    </div>
                    <div className="actions-inline">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowRegen(true);
                          setShowDisable(false);
                          setFormError('');
                          setPassword('');
                          setCode('');
                        }}
                      >
                        Regenerar códigos
                      </button>
                    </div>
                  </div>

                  <div className="status-row">
                    <div className="status-meta">
                      <strong>Desativar MFA</strong>
                      <span>Exige senha atual e um código do autenticador (ou de recuperação)</span>
                    </div>
                    <div className="actions-inline">
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          setShowDisable(true);
                          setShowRegen(false);
                          setFormError('');
                          setPassword('');
                          setCode('');
                        }}
                      >
                        Desativar
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="status-row">
                  <div className="status-meta">
                    <strong>Ativar MFA</strong>
                    <span>Escaneie um QR no autenticador e guarde códigos de backup</span>
                  </div>
                  <div className="actions-inline">
                    <Link
                      to="/settings/security/mfa/setup"
                      className="btn btn-primary btn-sm"
                      style={{ textDecoration: 'none' }}
                    >
                      Ativar MFA
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {(showDisable || showRegen) && (
            <div className="form-card" style={{ marginTop: 16 }}>
              <form onSubmit={showDisable ? handleDisable : handleRegen}>
                <div className="form-card-body">
                  <div className="form-section-title">
                    {showDisable ? 'Confirmar desativação' : 'Regenerar códigos de recuperação'}
                  </div>
                  <div className="hint-banner">
                    {showDisable
                      ? 'Preencha senha e código para desligar a verificação em duas etapas.'
                      : 'Os códigos atuais serão invalidados. Guarde os novos códigos com segurança.'}
                  </div>
                  {formError && (
                    <div className="auth-error" style={{ marginBottom: 14 }}>
                      <strong>Não foi possível continuar</strong>
                      {formError}
                    </div>
                  )}
                  <div className="form-grid">
                    <div className="field full">
                      <label htmlFor="mfa-password">Senha atual</label>
                      <input
                        id="mfa-password"
                        className="input"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        disabled={busy}
                      />
                    </div>
                    <div className="field full">
                      <label htmlFor="mfa-code">Código do autenticador ou recuperação</label>
                      <input
                        id="mfa-code"
                        className="input"
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        placeholder="000000"
                        disabled={busy}
                        style={{ maxWidth: 220, letterSpacing: '0.12em', fontWeight: 600 }}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-card-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForms}
                    disabled={busy}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={`btn ${showDisable ? 'btn-danger' : 'btn-primary'}`}
                    disabled={busy}
                  >
                    {busy
                      ? 'Processando…'
                      : showDisable
                        ? 'Confirmar desativação'
                        : 'Regenerar códigos'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SecuritySettings;
